import { useState } from 'react'
import { toast } from 'react-toastify'
import { useJobGroups } from '@/api/queries/job-group-queries'
import { CommitmentMatrixMultiSelector } from '@/components/administration/commitment-matrix/commitment-matrix-multi-selector'
import { Button } from '@/components/ui/button'
import { Field, FieldContent, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import type { JobGroupCluster } from '@/types'

export interface JobGroupClusterFormValues {
	name: string
	jobGroupIds: string[]
}

interface JobGroupClusterFormProps {
	mode: 'create' | 'edit'
	initial?: JobGroupCluster
	onSave: (values: JobGroupClusterFormValues) => void
	onClose: () => void
	isSaving?: boolean
}

export function JobGroupClusterFormContent({
	mode,
	initial,
	onSave,
	onClose,
	isSaving = false,
}: JobGroupClusterFormProps) {
	const [name, setName] = useState(initial?.name ?? '')
	const [jobGroupIds, setJobGroupIds] = useState<string[]>(
		initial?.jobGroupIds ?? [],
	)

	const { data: jobGroups, isLoading: isLoadingJobGroups } = useJobGroups()

	const jobGroupOptions = (jobGroups ?? []).map((jobGroup) => ({
		id: jobGroup.id,
		name: jobGroup.name,
	}))

	const handleSave = () => {
		if (!name.trim()) {
			toast.error('Informe um nome para o cluster.')
			return
		}

		onSave({ name: name.trim(), jobGroupIds })
	}

	return (
		<div className="flex flex-col gap-5 px-1">
			<Field>
				<FieldLabel htmlFor="job-group-cluster-name">
					Nome do cluster
				</FieldLabel>
				<FieldContent>
					<Input
						id="job-group-cluster-name"
						value={name}
						onChange={(e) => setName(e.target.value)}
						placeholder="Ex: SSMA"
						maxLength={120}
					/>
				</FieldContent>
			</Field>

			<Separator className="bg-border/60" />

			<div className="flex flex-col gap-3">
				<p className="text-xs text-muted-foreground">
					Selecione os grupos de cargo que pertencem a este cluster.
				</p>

				<CommitmentMatrixMultiSelector
					title="Grupos de cargo"
					options={jobGroupOptions}
					selected={jobGroupIds}
					onChange={setJobGroupIds}
					searchPlaceholder="Buscar grupo..."
					emptyMessage="Nenhum grupo de cargo encontrado"
					loading={isLoadingJobGroups}
					defaultOpen
				/>
			</div>

			<Separator className="bg-border/60" />

			<div className="flex gap-3 pb-1">
				<Button
					type="button"
					variant="cancel"
					onClick={onClose}
					className="flex-1"
					disabled={isSaving}
				>
					Cancelar
				</Button>
				<Button
					type="button"
					onClick={handleSave}
					disabled={isSaving}
					className="flex-1 bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90"
				>
					{isSaving
						? 'Salvando...'
						: mode === 'create'
							? 'Criar cluster'
							: 'Salvar alterações'}
				</Button>
			</div>
		</div>
	)
}
